import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, MessageSquare, Plus, Settings } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useFullHeightLayout } from '../../components/layout/AppShell';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/Spinner';
import {
  EMPTY_QUEUE_FILTER,
  CONVERSATION_PARAM,
  STATE_LABELS,
  STATE_VARIANTS,
  TRIGGER_LABELS,
  filterConversations,
  formatWhen,
  leadTrigger,
  pageIntroText,
  sortConversations,
  type QueueFilter,
} from '../../lib/leadBot/queueView';
import { getConversation, getLeadBotStatus, listConversations } from './api';
import { ConversationDetail } from './ConversationDetail';
import { NewLeadDialog } from './NewLeadDialog';
import { KC } from '../../lib/kyckConnect/links';

// SMS Lead Bot Phase 0 — the approval queue, which is also the conversation
// viewer.
//
// ⚠️ BUILT AS SOMETHING WORTH KEEPING, not a stopgap. Phase 3 reporting needs a
// transcript viewer and this is it; the only thing Phase 2 removes is the
// approval control, not the page.
//
// LAYOUT: viewport-bounded (useFullHeightLayout), the Community-inbox posture,
// so the approval panel is pinned to the bottom of the conversation column
// rather than sitting below a very tall page. ⚠️ Children of a scrolling column
// must be shrink-0 or the column's own overflow-hidden clips them instead of
// letting it scroll — the gotcha the inbox and publishing both document.

const POLL_MS = 15_000;

export function LeadBotPage() {
  useFullHeightLayout();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get(CONVERSATION_PARAM);
  const [filter, setFilter] = useState<QueueFilter>(EMPTY_QUEUE_FILTER);
  const [newLeadOpen, setNewLeadOpen] = useState(false);

  const { data: status } = useQuery({
    queryKey: ['lead-bot', 'status'],
    queryFn: getLeadBotStatus,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['lead-bot', 'conversations'],
    queryFn: listConversations,
    // ⚠️ Drafts arrive when a LEAD texts, not when anyone clicks — so the queue
    // has to poll to show up. This is a cheap Firestore read, NOT an AI call;
    // the arrival trigger lives on the server side of the Twilio webhook.
    refetchInterval: POLL_MS,
  });

  const pendingIds = useMemo(
    () => new Set(data?.pendingConversationIds ?? []),
    [data?.pendingConversationIds],
  );

  const rows = useMemo(() => {
    const conversations = data?.conversations ?? [];
    return sortConversations(filterConversations(conversations, filter, pendingIds), pendingIds);
  }, [data?.conversations, filter, pendingIds]);

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    next.set(CONVERSATION_PARAM, id);
    setParams(next, { replace: true });
  };

  const pendingCount = pendingIds.size;
  // Same query key as ConversationDetail, so this is the cached detail, not a second request.
  const { data: selectedDetail } = useQuery({
    queryKey: ['lead-bot', 'conversation', selectedId],
    queryFn: () => getConversation(selectedId!),
    enabled: Boolean(selectedId),
  });
  const selectedMode = selectedDetail?.config?.mode ?? null;

  return (
    // ROOT MUST BE h-full: <main> is a plain block box, so flex-1 here did nothing and the page grew
    // past the viewport (main then clips it with overflow-hidden), so no inner region ever overflowed
    // and the transcript could not scroll. h-full gives the whole chain a bounded height.
    <div className="flex h-full min-h-0 flex-col">
      {/* header */}
      <div className="shrink-0 border-b pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Approvals</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{pageIntroText(selectedMode)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={KC.botSettings}>
                <Settings className="h-4 w-4" />
                Bot settings
              </Link>
            </Button>
            <Button size="sm" onClick={() => setNewLeadOpen(true)}>
              <Plus className="h-4 w-4" />
              Add lead
            </Button>
          </div>
        </div>

        {status && !status.smsConfigured && (
          // ⚠️ Stated up front rather than discovered on a failed send: the
          // bot's Twilio number is provisioned separately from everything else.
          <div className="mt-3 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              The lead bot has no SMS number yet, so nothing can be sent. Everything else
              works — leads, drafts and approvals all queue up here and can be sent the moment
              the number is provisioned.
            </p>
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-4 pt-3">
        {/* queue */}
        <div className="flex w-80 min-h-0 shrink-0 flex-col rounded-lg border">
          <div className="shrink-0 space-y-2 border-b p-3">
            <Input
              value={filter.search}
              onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search name, number or client…"
              className="h-8"
              aria-label="Search conversations"
            />
            <div className="flex flex-wrap gap-1.5">
              <FilterChip
                label={`Awaiting approval${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
                active={filter.pendingOnly}
                onClick={() => setFilter((f) => ({ ...f, pendingOnly: !f.pendingOnly }))}
              />
              <FilterChip
                label="Needs a person"
                active={filter.handoffOnly}
                onClick={() => setFilter((f) => ({ ...f, handoffOnly: !f.handoffOnly }))}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto scroll-subtle">
            {isLoading && (
              <div className="flex justify-center p-6">
                <Spinner />
              </div>
            )}
            {!isLoading && rows.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">
                No conversations yet. Add a lead to start one.
              </p>
            )}
            {rows.map((conversation) => {
              const trigger = leadTrigger(conversation.handoffTriggers);
              const pending = pendingIds.has(conversation.id);
              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => select(conversation.id)}
                  className={[
                    'block w-full border-b px-3 py-2.5 text-left transition-colors hover:bg-accent/40',
                    selectedId === conversation.id ? 'bg-accent/60' : '',
                    // A pending draft is the thing blocking the AM, so it gets
                    // the only accent on the row.
                    pending ? 'border-l-2 border-l-kyck-600' : 'border-l-2 border-l-transparent',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate text-sm font-medium">
                      {conversation.leadName}
                      {conversation.intakeTest === true && <span data-testid="test-badge" className="ml-2 rounded border px-1.5 py-0.5 text-xs font-normal text-muted-foreground">Test</span>}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatWhen(conversation.updatedAt)}
                    </span>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {conversation.accountName ?? conversation.leadPhone}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {pending && (
                      <Badge variant="info">
                        <MessageSquare className="h-3 w-3" />
                        Draft waiting
                      </Badge>
                    )}
                    {conversation.state !== 'active' && (
                      <Badge variant={STATE_VARIANTS[conversation.state]}>
                        {STATE_LABELS[conversation.state]}
                      </Badge>
                    )}
                    {trigger?.kind === 'compliance' && (
                      <Badge variant="danger" title={trigger.detail}>
                        {TRIGGER_LABELS.compliance}
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* detail */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border">
          {selectedId ? (
            <ConversationDetail
              key={selectedId}
              conversationId={selectedId}
              smsConfigured={status?.smsConfigured ?? false}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground">
              Pick a conversation to read the thread and approve its reply.
            </div>
          )}
        </div>
      </div>

      <NewLeadDialog
        open={newLeadOpen}
        onOpenChange={setNewLeadOpen}
        onCreated={select}
      />
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
        active
          ? 'border-kyck-600 bg-kyck-50 text-kyck-700'
          : 'border-slate-200 text-slate-600 hover:bg-accent/40',
      ].join(' ')}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
